export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string };
    };
    return new ApiError(
      res.status,
      body.error?.code ?? 'http_error',
      body.error?.message ?? res.statusText,
    );
  } catch {
    return new ApiError(res.status, 'http_error', res.statusText);
  }
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  return fetch(path, {
    ...init,
    credentials: 'include',
    headers,
  });
}

export async function login(password: string): Promise<void> {
  const res = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  if (!res.ok) throw await parseError(res);
}

export async function logout(): Promise<void> {
  await apiFetch('/auth/logout', { method: 'POST' });
}

/** Lightweight session probe — never used to block the form. */
export async function sessionLooksValid(): Promise<boolean> {
  try {
    const res = await apiFetch('/clients?active=true');
    return res.ok;
  } catch {
    return false;
  }
}

export type RemoteXeroContactMatch = {
  ContactID: string;
  Name: string;
  EmailAddress?: string | null;
};

export type RemoteClient = {
  id: string;
  name: string;
  day_rate: number | string;
  half_day_rate: number | string;
  hourly_rate: number | string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  address?: string | null;
  payment_terms_days?: number | null;
  external_contact_id?: string | null;
  xero_contact_status?:
    | 'mapped'
    | 'unmapped'
    | 'pending'
    | 'conflict'
    | 'failed';
  xero_contact_error?: string | null;
  xero_contact_matches?: RemoteXeroContactMatch[] | null;
};

export type RemoteRateOverride = {
  id: string;
  client_id: string;
  work_type_id: string;
  day_rate: number | string;
  half_day_rate: number | string;
  hourly_rate: number | string | null;
};

export type RemoteWorkType = {
  id: string;
  code: string;
  short_description: string;
  line_kind: 'day_rate' | 'fixed_fee' | 'expense';
  invoice_narrative?: string | null;
  half_day_suffix?: string | null;
};

export type RemoteSite = {
  id: string;
  name: string;
  client_id: string | null;
  use_count?: number;
};

export async function fetchClients(): Promise<RemoteClient[]> {
  const res = await apiFetch('/clients?active=true');
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { data: RemoteClient[] };
  return body.data;
}

export async function fetchRateOverrides(): Promise<RemoteRateOverride[]> {
  const res = await apiFetch('/clients/rate-overrides');
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { data: RemoteRateOverride[] };
  return body.data;
}

export async function fetchWorkTypes(): Promise<RemoteWorkType[]> {
  const res = await apiFetch('/work-types?active=true');
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { data: RemoteWorkType[] };
  return body.data;
}

export async function fetchSites(): Promise<RemoteSite[]> {
  const res = await apiFetch('/sites?active=true');
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { data: RemoteSite[] };
  return body.data;
}

export type CreateClientBody = {
  id?: string;
  name: string;
  day_rate: number;
  half_day_rate: number;
  hourly_rate?: number | null;
  contact_name?: string | null;
  contact_email?: string | null;
  address?: string | null;
  payment_terms_days?: number;
};

export async function createClientRemote(
  body: CreateClientBody,
): Promise<RemoteClient> {
  const res = await apiFetch('/clients', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to create a client');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteClient }).data;
}

export async function pushClientXeroRemote(
  id: string,
): Promise<RemoteClient> {
  const res = await apiFetch(`/clients/${id}/xero`, {
    method: 'POST',
    body: JSON.stringify({ action: 'push' }),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to push the Xero contact');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteClient }).data;
}

export async function linkClientXeroRemote(
  id: string,
  contactId: string,
): Promise<RemoteClient> {
  const res = await apiFetch(`/clients/${id}/xero`, {
    method: 'POST',
    body: JSON.stringify({ action: 'link', contact_id: contactId }),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to link the Xero contact');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteClient }).data;
}

export async function createSite(body: {
  name: string;
  client_id?: string | null;
}): Promise<RemoteSite> {
  const res = await apiFetch('/sites', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to create a site');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteSite }).data;
}

export type BatchJobPayload = {
  id: string;
  work_date: string;
  client_id: string;
  work_type_id: string;
  duration_type: 'full_day' | 'half_day' | 'custom';
  custom_hours?: number | null;
  custom_amount?: number | null;
  site_reference?: string | null;
  site_ids?: string[];
  billable?: boolean;
  /** Omit → server treats as completed (stale clients). Calendar sends planned. */
  status?: 'planned' | 'completed' | 'cancelled';
  start_time?: string | null;
  po_number?: string | null;
  notes?: string | null;
};

export type BatchItemResult = {
  id: string;
  ok: boolean;
  data?: { invoice_run_id?: string | null };
  error?: { code: string; message: string };
};

export async function postJobsBatch(
  jobs: BatchJobPayload[],
): Promise<BatchItemResult[]> {
  const res = await apiFetch('/jobs/batch', {
    method: 'POST',
    body: JSON.stringify({ jobs }),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { results: BatchItemResult[] };
  return body.results;
}

export type RemoteJob = {
  id: string;
  work_date: string;
  client_id: string;
  work_type_id: string;
  duration_type: 'full_day' | 'half_day' | 'custom';
  custom_hours: number | string | null;
  custom_amount: number | string | null;
  site_reference: string | null;
  site_ids?: string[];
  po_number: string | null;
  notes: string | null;
  billable: boolean;
  status: 'planned' | 'completed' | 'cancelled';
  start_time: string | null;
  confirmed_at: string | null;
  released_at?: string | null;
  xero_invoice_id?: string | null;
  billed?: boolean;
  invoice_run_id: string | null;
  invoice_number?: string | null;
  created_at: string;
  updated_at: string;
  recharge_receipt_id?: string | null;
};

export type WeekSignoffJob = {
  id: string;
  work_date: string;
  client_id: string;
  site_label: string;
  duration_type: string;
  duration_label: string;
  line_kind?: string;
  rate_pence: number | null;
  amount_pence: number | null;
  narrative: string;
  released_at: string | null;
  carried_forward: boolean;
  rate_error: string | null;
};

export type WeekAttentionJob = {
  id: string;
  work_date: string;
  client_id: string;
  client_name: string;
  site_label: string;
  duration_label: string;
  narrative: string;
  status: 'planned' | 'completed';
  billable: boolean;
};

export type WeekSignoffClientGroup = {
  client_id: string;
  client_name: string;
  total_pence: number;
  job_count: number;
  pending_count: number;
  rate_blocked: boolean;
  can_release: boolean;
  jobs: WeekSignoffJob[];
};

export type WeekSignoffSummary = {
  week: { start: string; end: string };
  today: string;
  saturday_approaching: boolean;
  pending_count: number;
  pending_pence: number;
  pending_days: number;
  released_count: number;
  total_pence: number;
  ready: boolean;
  release_warning: string | null;
  clients: WeekSignoffClientGroup[];
  provisional: WeekAttentionJob[];
  non_billable: WeekAttentionJob[];
};

export type WeekSignoffBanner = {
  week: { start: string; end: string };
  pending_count: number;
  pending_pence?: number;
  pending_days?: number;
  saturday_approaching: boolean;
  show_banner: boolean;
};

export async function fetchWeekSignoff(
  weekStart?: string,
): Promise<WeekSignoffSummary> {
  const q = new URLSearchParams();
  if (weekStart) q.set('week_start', weekStart);
  const res = await apiFetch(`/jobs/week-signoff?${q}`);
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: WeekSignoffSummary }).data;
}

export async function fetchWeekSignoffBanner(): Promise<WeekSignoffBanner> {
  const res = await apiFetch('/jobs/week-signoff-banner');
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: WeekSignoffBanner }).data;
}

export async function releaseWeek(
  weekStart: string,
  opts: { clientId?: string } = {},
): Promise<{
  released: number;
  ids: string[];
  skipped_blocked: number;
  summary: WeekSignoffSummary;
}> {
  const res = await apiFetch('/jobs/release-week', {
    method: 'POST',
    body: JSON.stringify({ week_start: weekStart, client_id: opts.clientId }),
  });
  if (!res.ok) throw await parseError(res);
  return (
    (await res.json()) as {
      data: {
        released: number;
        ids: string[];
        skipped_blocked: number;
        summary: WeekSignoffSummary;
      };
    }
  ).data;
}

export async function unreleaseWeek(
  weekStart: string,
  opts: { clientId?: string; jobId?: string } = {},
): Promise<{
  unreleased: number;
  ids: string[];
  blocked: number;
  summary: WeekSignoffSummary;
}> {
  const res = await apiFetch('/jobs/unrelease-week', {
    method: 'POST',
    body: JSON.stringify({
      week_start: weekStart,
      client_id: opts.clientId,
      job_id: opts.jobId,
    }),
  });
  if (!res.ok) throw await parseError(res);
  return (
    (await res.json()) as {
      data: {
        unreleased: number;
        ids: string[];
        blocked: number;
        summary: WeekSignoffSummary;
      };
    }
  ).data;
}

export type XeroInvoiceSyncResult = {
  checked: number;
  billed: number;
  paid: number;
  authorised: number;
  reverted: number;
  voided: number;
  stillDraft: number;
  missing: number;
  fetched: number;
  imported: number;
  unmatched: number;
  unmatched_invoice_numbers: string[];
  skipped_deleted: number;
  skipped_existing: number;
  skipped_number_clash: number;
  errors: string[];
  message: string;
};

export async function syncXeroInvoices(): Promise<XeroInvoiceSyncResult> {
  const res = await apiFetch('/xero/sync-invoices', { method: 'POST' });
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: XeroInvoiceSyncResult }).data;
}

export async function fetchJobs(params: {
  from?: string;
  to?: string;
  limit?: string;
  offset?: string;
}): Promise<RemoteJob[]> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.limit) q.set('limit', params.limit);
  if (params.offset) q.set('offset', params.offset);
  const res = await apiFetch(`/jobs?${q}`);
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { data: RemoteJob[] };
  return body.data;
}

/** Page through /jobs until exhausted (historical + future). */
export async function fetchAllJobs(params: {
  from?: string;
  to?: string;
} = {}): Promise<RemoteJob[]> {
  const out: RemoteJob[] = [];
  const limit = 500;
  let offset = 0;
  for (;;) {
    const batch = await fetchJobs({
      ...params,
      limit: String(limit),
      offset: String(offset),
    });
    out.push(...batch);
    if (batch.length < limit) break;
    offset += limit;
    if (offset > 50_000) break;
  }
  return out;
}

export async function deleteJobRemote(id: string): Promise<void> {
  const res = await apiFetch(`/jobs/${id}`, { method: 'DELETE' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (res.status === 404) return;
  if (!res.ok) throw await parseError(res);
}

export type RemoteCompliance = {
  id: string;
  category: string;
  title: string;
  subject_reference: string | null;
  issuer: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  lead_time_days: number;
  cost_pence: number | null;
  billing_frequency: 'monthly' | 'annual' | 'one_off';
  notes: string | null;
  document_reference: string | null;
  active: boolean;
};

export async function fetchCompliance(): Promise<RemoteCompliance[]> {
  const res = await apiFetch('/compliance?active=true');
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { data: RemoteCompliance[] };
  return body.data;
}

export async function fetchComplianceDue(): Promise<RemoteCompliance[]> {
  const res = await apiFetch('/compliance/due');
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { data: RemoteCompliance[] };
  return body.data;
}

export async function createComplianceRemote(
  payload: Record<string, unknown>,
): Promise<RemoteCompliance> {
  const res = await apiFetch('/compliance', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteCompliance }).data;
}

export async function patchComplianceRemote(
  id: string,
  payload: Record<string, unknown>,
): Promise<RemoteCompliance> {
  const res = await apiFetch(`/compliance/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteCompliance }).data;
}

export async function deleteComplianceRemote(id: string): Promise<void> {
  const res = await apiFetch(`/compliance/${id}`, { method: 'DELETE' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
}

export async function renewComplianceRemote(
  id: string,
  payload: { event_date?: string; new_expiry_date?: string | null; notes?: string | null } = {},
): Promise<{ item: RemoteCompliance }> {
  const res = await apiFetch(`/compliance/${id}/renew`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return (await res.json()).data as { item: RemoteCompliance };
}

export type RemoteExpense = {
  id: string;
  name: string;
  category: string | null;
  amount_pence: number;
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'annual';
  notes: string | null;
  active: boolean;
};

export async function fetchExpenses(): Promise<RemoteExpense[]> {
  const res = await apiFetch('/expenses?active=true');
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { data: RemoteExpense[] };
  return body.data;
}

export async function createExpenseRemote(
  payload: Record<string, unknown>,
): Promise<RemoteExpense> {
  const res = await apiFetch('/expenses', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteExpense }).data;
}

export async function patchExpenseRemote(
  id: string,
  payload: Record<string, unknown>,
): Promise<RemoteExpense> {
  const res = await apiFetch(`/expenses/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteExpense }).data;
}

export async function deleteExpenseRemote(id: string): Promise<void> {
  const res = await apiFetch(`/expenses/${id}`, { method: 'DELETE' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
}

export type RemoteReceipt = {
  id: string;
  status: string;
  captured_at: string;
  mime_type: string;
  supplier_name: string | null;
  receipt_date: string | null;
  currency?: string | null;
  net_pence: number | null;
  vat_pence: number | null;
  gross_pence: number | null;
  gbp_net_pence?: number | null;
  gbp_vat_pence?: number | null;
  gbp_gross_pence?: number | null;
  fx_rate?: number | null;
  fx_rate_as_of?: string | null;
  fx_source?: string | null;
  fx_period?: string | null;
  category: string | null;
  supplier_vat_number?: string | null;
  vat_evidence_state?: string | null;
  notes: string | null;
  has_thumb?: boolean;
  confirmed_at?: string | null;
  extraction_error?: string | null;
  extraction_cost_usd?: number | null;
  extraction_attempts?: number;
  money_reconcile_ok?: boolean | null;
  money_reconcile_note?: string | null;
  money_reconcile_overridden?: boolean | null;
  money_reconcile_overridden_at?: string | null;
  low_confidence_fields?: string[];
  fields_edited?: string[];
  extraction_suggested?: {
    confidence?: Record<string, string>;
    category_source?: string;
  } | null;
  rechargeable?: boolean | null;
  client_id?: string | null;
  site_id?: string | null;
  recharge_job_id?: string | null;
  recharge_frozen?: boolean;
  recharge_job?: RemoteJob | null;
  xero_bill_id?: string | null;
  xero_attachment_status?: string | null;
  xero_push_error?: string | null;
  xero_push_outcome?: string | null;
  push_display_state?: string | null;
  xero_invoice_number?: string | null;
  xero_contact_id?: string | null;
  xero_contact_name?: string | null;
  xero_bill_status?: string | null;
  xero_amount_due_pence?: number | null;
  xero_has_attachments?: boolean | null;
  xero_total_mismatch?: boolean;
  xero_contact_map_mismatch?: boolean;
  xero_date_mismatch?: boolean;
  xero_bill_date?: string | null;
  receipt_date_weekday?: string | null;
  receipt_date_month_named?: boolean | null;
  receipt_date_facts_known?: boolean;
  archived_at?: string | null;
  recharge_decision?: string | null;
  manual_resolved_at?: string | null;
  manual_resolve_reason?: string | null;
  manual_resolve_note?: string | null;
  field_sources?: Record<string, string>;
  held?: boolean;
  held_reason?: string | null;
  held_at?: string | null;
};

export async function uploadReceiptRemote(
  id: string,
  blob: Blob,
  opts: { captured_at?: string; filename?: string; mimeType?: string } = {},
): Promise<RemoteReceipt> {
  const { prepareReceiptUploadFile } = await import(
    '../receipts/prepareUpload.js'
  );
  const file = await prepareReceiptUploadFile(blob, {
    mimeType: opts.mimeType || blob.type,
    filename: opts.filename,
  });
  const fd = new FormData();
  fd.set('id', id);
  fd.set('file', file, file.name);
  if (opts.captured_at) fd.set('captured_at', opts.captured_at);
  const res = await fetch('/receipts/upload', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt }).data;
}

export async function fetchReceipts(
  query: Record<string, string> = {},
): Promise<RemoteReceipt[]> {
  const qs = new URLSearchParams(query).toString();
  const res = await apiFetch(`/receipts${qs ? `?${qs}` : ''}`);
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt[] }).data;
}

export async function fetchReceiptCategories(): Promise<ReceiptCategoryOption[]> {
  const res = await apiFetch('/receipts/categories');
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: ReceiptCategoryOption[] }).data;
}

export type ReceiptCategoryOption = {
  id: string;
  label: string;
  vat_recovery_blocked?: boolean;
  accountant_confirm_needed?: boolean;
};

export async function fetchReceipt(id: string): Promise<RemoteReceipt> {
  const res = await apiFetch(`/receipts/${id}`);
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt }).data;
}

export async function patchReceiptRemote(
  id: string,
  payload: Record<string, unknown>,
): Promise<RemoteReceipt> {
  const res = await apiFetch(`/receipts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt }).data;
}

export async function deleteReceiptRemote(id: string): Promise<void> {
  const res = await apiFetch(`/receipts/${id}`, { method: 'DELETE' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
}

export type ReceiptRechargeResult = {
  receipt: RemoteReceipt;
  job: RemoteJob | null;
};

export async function rechargeReceiptRemote(
  id: string,
  payload: { client_id: string; site_id?: string | null } | { skip: true },
): Promise<ReceiptRechargeResult> {
  const res = await apiFetch(`/receipts/${id}/recharge`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: ReceiptRechargeResult }).data;
}

export async function unRechargeReceiptRemote(
  id: string,
): Promise<ReceiptRechargeResult> {
  const res = await apiFetch(`/receipts/${id}/recharge`, { method: 'DELETE' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: ReceiptRechargeResult }).data;
}

export type RemoteSupplierContactMatch = {
  ContactID: string;
  Name: string;
  EmailAddress?: string | null;
};

export type RemoteSupplierContact = {
  status:
    | 'mapped'
    | 'would_create'
    | 'needs_confirm'
    | 'approved_create'
    | 'failed';
  contact_id: string | null;
  name: string;
  matches: RemoteSupplierContactMatch[];
  error: string | null;
  payload_contact: { ContactID: string } | { Name: string } | null;
};

export async function fetchSupplierContactRemote(
  id: string,
): Promise<RemoteSupplierContact> {
  const res = await apiFetch(`/receipts/${id}/supplier-contact`);
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteSupplierContact }).data;
}

export async function applySupplierContactRemote(
  id: string,
  payload:
    | { action: 'link'; contact_id: string }
    | { action: 'reject' }
    | { action: 'approve_create' },
): Promise<RemoteSupplierContact> {
  const res = await apiFetch(`/receipts/${id}/supplier-contact`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteSupplierContact }).data;
}

export type RemoteAccpayConfirmation = {
  liveReady: boolean;
  liveBlockReason: string | null;
  supplier: string;
  contactAction: 'mapped' | 'create' | 'blocked';
  contactLabel: string;
  accountCode: string | null;
  accountLabel: string | null;
  taxType: string | null;
  taxLabel: string | null;
  netPence: number | null;
  vatPence: number | null;
  grossPence: number | null;
  gbpNetPence?: number | null;
  gbpVatPence?: number | null;
  gbpGrossPence?: number | null;
  currency: string | null;
  billId: string | null;
  attachmentStatus: string | null;
  xeroUrl: string | null;
  xeroPushError: string | null;
  pushOutcome: string | null;
};

export type RemoteAccpayPreview = RemoteAccpayConfirmation & {
  created: boolean;
};

export type RemoteAccpayPushResult = {
  created: boolean;
  billId: string | null;
  contactCreated: boolean;
  attachmentStatus: string;
  error: string | null;
  xeroUrl: string | null;
  confirmation: RemoteAccpayConfirmation;
};

export async function fetchAccpayPreviewRemote(
  id: string,
): Promise<RemoteAccpayPreview> {
  const res = await apiFetch(`/receipts/${id}/accpay-preview`);
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as {
    data: { confirmation: RemoteAccpayConfirmation; created: boolean };
  };
  return { ...body.data.confirmation, created: body.data.created };
}

export async function setReceiptHoldRemote(
  id: string,
  reason: string,
): Promise<RemoteReceipt> {
  const res = await apiFetch(`/receipts/${id}/hold`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt }).data;
}

export async function clearReceiptHoldRemote(id: string): Promise<RemoteReceipt> {
  const res = await apiFetch(`/receipts/${id}/hold`, { method: 'DELETE' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt }).data;
}

export async function setManualResolveRemote(
  id: string,
  body: { reason: string; note: string | null },
): Promise<RemoteReceipt> {
  const res = await apiFetch(`/receipts/${id}/manual-resolve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt }).data;
}

export async function clearManualResolveRemote(
  id: string,
): Promise<RemoteReceipt> {
  const res = await apiFetch(`/receipts/${id}/manual-resolve`, {
    method: 'DELETE',
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt }).data;
}

export async function setReceiptArchivedRemote(
  id: string,
  archived: boolean,
): Promise<RemoteReceipt> {
  const res = await apiFetch(`/receipts/${id}/archive`, {
    method: archived ? 'POST' : 'DELETE',
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteReceipt }).data;
}

export async function sendReceiptAccpayRemote(
  id: string,
  opts?: { createAnyway?: boolean },
): Promise<RemoteAccpayPushResult> {
  const res = await apiFetch(`/receipts/${id}/accpay`, {
    method: 'POST',
    body: JSON.stringify({
      confirm: true,
      ...(opts?.createAnyway ? { create_anyway: true } : {}),
    }),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteAccpayPushResult }).data;
}

export async function flushReceiptAccpayQueueRemote(): Promise<{
  flushed: number;
  errors: number;
}> {
  const res = await apiFetch('/receipts/accpay-queue', { method: 'POST' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: { flushed: number; errors: number } })
    .data;
}

export type ReceiptCategoryTotal = {
  category: string;
  gross_pence: number;
  vat_pence: number;
  count: number;
};

export type ReceiptTotals = {
  month: {
    from: string;
    to: string;
    net_pence: number;
    vat_pence: number;
    gross_pence: number;
    count: number;
  };
  vat_quarter: {
    from: string;
    to: string;
    label: string;
    net_pence: number;
    vat_pence: number;
    gross_pence: number;
    count: number;
  };
  last_12: {
    from: string;
    to: string;
    net_pence: number;
    vat_pence: number;
    gross_pence: number;
    count: number;
  };
  by_category: {
    month: ReceiptCategoryTotal[];
    vat_quarter: ReceiptCategoryTotal[];
    last_12: ReceiptCategoryTotal[];
  };
};

export async function fetchReceiptTotals(
  month?: string,
): Promise<ReceiptTotals> {
  const qs = month ? `?month=${encodeURIComponent(month)}` : '';
  const res = await apiFetch(`/receipts/totals${qs}`);
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: ReceiptTotals }).data;
}

export type XeroStatus = {
  status: string;
  xero_tenant_id: string | null;
  xero_tenant_name: string | null;
  scopes: string | null;
  access_token_expires_at: string | null;
  last_refresh_at: string | null;
  last_refresh_error: string | null;
  last_health_ok_at: string | null;
  connected_at: string | null;
  missing_env?: string[];
};

export async function fetchXeroStatus(): Promise<XeroStatus> {
  const res = await apiFetch('/xero/status');
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: XeroStatus }).data;
}

export async function disconnectXeroRemote(): Promise<void> {
  const res = await apiFetch('/xero/disconnect', { method: 'POST' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
}

export type RemoteInvoice = {
  id: string;
  invoice_number: string | null;
  client_id: string | null;
  client_name: string;
  invoice_date: string;
  due_date: string | null;
  sent_at: string | null;
  paid_at: string | null;
  status: string;
  net_pence: number | null;
  vat_pence: number | null;
  gross_pence: number | null;
  amount_due_pence: number | null;
  remaining_pence: number;
  xero_invoice_id: string | null;
  not_in_xero: boolean;
  unmatched_contact: boolean;
  xero_contact_id: string | null;
  adapter?: string;
  days_since_sent: number | null;
  days_overdue: number | null;
  days_to_payment: number | null;
};

export type RemoteInvoiceDetail = RemoteInvoice & {
  lines: Array<{
    id: string | null;
    work_date: string | null;
    description: string;
    net_pence: number | null;
    source: string;
  }>;
};

export type InvoiceSummary = {
  outstanding_pence: number;
  overdue_pence: number;
  not_in_xero: {
    pence: number;
    invoices: Array<{
      id: string;
      invoice_number: string | null;
      client_name: string;
      remaining_pence: number;
      due_date: string | null;
      days_overdue: number | null;
    }>;
  };
  unmatched_contacts: Array<{
    id: string;
    invoice_number: string | null;
    client_name: string;
    remaining_pence: number;
    due_date: string | null;
  }>;
  last_synced_at: string | null;
  month: { from: string; to: string; invoiced_pence: number };
  vat_quarter: {
    from: string;
    to: string;
    label: string;
    invoiced_pence: number;
  };
  per_client: Array<{
    client_id: string | null;
    client_name: string;
    outstanding_pence: number;
    overdue_pence: number;
    invoiced_pence: number;
    paid_count: number;
    avg_days_to_payment: number | null;
  }>;
};

export type InvoiceAnalytics = {
  period: {
    id: string;
    from: string | null;
    to: string;
    label: string;
  };
  avg_days_to_payment: {
    ready: Array<{
      client_id: string | null;
      client_name: string;
      avg_days_to_payment: number;
      paid_count: number;
    }>;
    insufficient: Array<{
      client_id: string | null;
      client_name: string;
      paid_count: number;
    }>;
    min_paid_invoices: number;
  };
  work_type_by_client: Array<{
    client_id: string;
    client_name: string;
    total_pence: number;
    mix: Array<{ code: string; label: string; pence: number; pct: number }>;
  }>;
  work_type_overall: Array<{
    code: string;
    label: string;
    pence: number;
    pct: number;
  }>;
  work_type_by_year: Array<{
    year: number;
    total_pence: number;
    mix: Array<{ code: string; label: string; pence: number; pct: number }>;
  }>;
  by_month: Array<{ month: string; pence: number }>;
  concentration: Array<{
    name: string;
    pence: number;
    pct: number;
    over_threshold: boolean;
  }>;
  concentration_threshold: number;
  group_related: boolean;
  family_groups: Array<{ id: string; label: string; names: string[] }>;
  total_invoiced_pence: number;
};

export async function fetchInvoices(
  query: Record<string, string> = {},
): Promise<RemoteInvoice[]> {
  const qs = new URLSearchParams(query).toString();
  const res = await apiFetch(`/invoices${qs ? `?${qs}` : ''}`);
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteInvoice[] }).data;
}

export async function fetchInvoice(id: string): Promise<RemoteInvoiceDetail> {
  const res = await apiFetch(`/invoices/${id}`);
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteInvoiceDetail }).data;
}

export async function linkInvoiceClient(
  invoiceId: string,
  clientId: string,
): Promise<{ client_id: string; client_name: string }> {
  const res = await apiFetch(`/invoices/${invoiceId}/link-client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_id: clientId }),
  });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: { client_id: string; client_name: string } })
    .data;
}

export async function undraftInvoice(
  id: string,
): Promise<{ jobs: number; ids: string[] }> {
  const res = await apiFetch(`/invoices/${id}/undraft`, { method: 'POST' });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: { jobs: number; ids: string[] } }).data;
}

export async function fetchInvoiceSummary(): Promise<InvoiceSummary> {
  const res = await apiFetch('/invoices/summary');
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: InvoiceSummary }).data;
}

export async function fetchInvoiceAnalytics(opts: {
  period?: string;
  group_related?: boolean;
}): Promise<InvoiceAnalytics> {
  const qs = new URLSearchParams();
  if (opts.period) qs.set('period', opts.period);
  if (opts.group_related) qs.set('group_related', 'true');
  const res = await apiFetch(
    `/invoices/analytics${qs.toString() ? `?${qs}` : ''}`,
  );
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: InvoiceAnalytics }).data;
}

export async function fetchOverdueInvoices(): Promise<RemoteInvoice[]> {
  const res = await apiFetch('/invoices/overdue');
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as { data: RemoteInvoice[] }).data;
}

export type {
  NotificationItem,
  NotificationsPayload,
} from '../notifications/types.js';

export async function fetchNotifications(): Promise<
  import('../notifications/types.js').NotificationsPayload
> {
  const res = await apiFetch('/notifications');
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(res.status, 'unauthenticated', 'Sign in to sync');
  }
  if (!res.ok) throw await parseError(res);
  return (
    (await res.json()) as {
      data: import('../notifications/types.js').NotificationsPayload;
    }
  ).data;
}

export function isTransientHttp(status: number): boolean {
  return status === 0 || status === 401 || status === 403 || status >= 500;
}

export function isPermanentHttp(status: number): boolean {
  return status >= 400 && status < 500 && status !== 401 && status !== 403;
}
